import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";
import api from "../../services/api";
import "./cssDash/ProtocoleDashboard.css";

interface Mariage {
  _id: string;
  bride: string;
  groom: string;
  date: string;
  heure: string;
  lieu: string;
}

interface Evenement {
  _id: string;
  type: string;
  dateISO: string;
  heureISO: string;
  lieu: string;
}

interface Anniversaire {
  _id: string;
  personne: string;
  age: number;
  dateISO: string;
  heureISO: string;
  lieu: string;
}

interface InviteArrive {
  _id: string;
  nom: string;
  heure: string;
  statut: string;
}

interface InviteAttendu {
  _id: string;
  nom: string;
}

interface TableData {
  name: string;
  total: number;
  arrives: InviteArrive[];
  attendus: InviteAttendu[];
}

interface User {
  _id: string;
  name: string;
  role: string;
}

interface NotificationData {
  message: string;
  invite: {
    nom: string;
    table: string;
    heure: string;
  };
}

interface AuthResponse {
  success: boolean;
  user: User;
}

interface MariagesResponse {
  success: boolean;
  mariages: Mariage[];
  message?: string;
}

interface EvenementsResponse {
  success: boolean;
  evenements: Evenement[];
  message?: string;
}

interface AnniversairesResponse {
  success: boolean;
  anniversaires: Anniversaire[];
  message?: string;
}

interface TablesResponse {
  success: boolean;
  tables: TableData[];
  message?: string;
}

interface InstallResponse {
  success: boolean;
  invite: {
    nom: string;
  };
  message?: string;
}

type EventType = "mariages" | "evenements" | "anniversaires";

const API_URL = process.env.REACT_APP_API_URL;

const ProtocoleDashboard: React.FC = () => {
  const [eventType, setEventType] = useState<EventType>("mariages");
  const [mariages, setMariages] = useState<Mariage[]>([]);
  const [evenements, setEvenements] = useState<Evenement[]>([]);
  const [anniversaires, setAnniversaires] = useState<Anniversaire[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tables, setTables] = useState<TableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTables, setLoadingTables] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTable, setExpandedTable] = useState<string | null>(null);
  const [notification, setNotification] = useState<NotificationData | null>(null);
  const navigate = useNavigate();
  const [socket, setSocket] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Date non définie";
    
    try {
      if (dateString.includes('-')) {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR');
        }
      }
      
      const moisMap: { [key: string]: number } = {
        'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3,
        'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7,
        'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
      };
      
      const mots = dateString.toLowerCase().split(' ');
      const jour = parseInt(mots[0]);
      const moisNom = mots[1];
      const annee = parseInt(mots[2]);
      
      if (moisMap[moisNom] !== undefined && !isNaN(jour) && !isNaN(annee)) {
        const date = new Date(annee, moisMap[moisNom], jour);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR');
        }
      }
      
      return "Date invalide";
    } catch {
      return "Date invalide";
    }
  };

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await api.get<AuthResponse>("/auth/me");
      if (response.data.success) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error("Erreur chargement user:", error);
    }
  };

  useEffect(() => {
    fetchUser();
    
    const newSocket = io(API_URL);
    setSocket(newSocket);

    return () => {
      if (newSocket) newSocket.disconnect();
    };
  }, []);

  const fetchTables = useCallback(async (id: string) => {
    setLoadingTables(true);
    try {
      let url = "";
      if (eventType === "mariages") {
        url = `/protocole/tables/${id}`;
      } else if (eventType === "evenements") {
        url = `/protocole/evenements/${id}/tables`;
      } else {
        url = `/protocole/anniversaires/${id}/tables`;
      }
      
      const response = await api.get<TablesResponse>(url);

      if (response.data.success) {
        setTables(response.data.tables);
      } else {
        setError(response.data.message || "Erreur lors du chargement des tables");
      }
    } catch (error) {
      console.error("Erreur chargement tables:", error);
      setError("Erreur de connexion au serveur");
    } finally {
      setLoadingTables(false);
    }
  }, [eventType]);

  useEffect(() => {
    if (socket && user && selectedId && eventType) {
      const eventKey = `${eventType.slice(0, -1)}Id`;
      socket.emit('register-protocole', {
        protocoleId: user._id,
        [eventKey]: selectedId
      });
      
      socket.on('nouvel-arrive', (data: NotificationData) => {
        setNotification(data);
        
        if (selectedId) {
          fetchTables(selectedId);
        }
        
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      });
    }
    
    return () => {
      if (socket) {
        socket.off('nouvel-arrive');
      }
    };
  }, [socket, user, selectedId, eventType, fetchTables]);

  const fetchMariages = useCallback(async () => {
    try {
      const response = await api.get<MariagesResponse>("/protocole/mariages");

      if (response.data.success) {
        setMariages(response.data.mariages);
        if (response.data.mariages.length === 1 && eventType === "mariages") {
          const firstId = response.data.mariages[0]._id;
          setSelectedId(firstId);
          fetchTables(firstId);
        }
      } else {
        setError(response.data.message || "Erreur lors du chargement");
      }
    } catch (error: any) {
      console.error("Erreur chargement mariages:", error);
      if (error.response?.status === 403) {
        setError("Session expirée. Veuillez vous reconnecter.");
      } else {
        setError("Erreur de connexion au serveur");
      }
    }
  }, [eventType, fetchTables]);

  const fetchEvenements = useCallback(async () => {
    try {
      const response = await api.get<EvenementsResponse>("/protocole/evenements");

      if (response.data.success) {
        setEvenements(response.data.evenements);
        if (response.data.evenements.length === 1 && eventType === "evenements") {
          const firstId = response.data.evenements[0]._id;
          setSelectedId(firstId);
          fetchTables(firstId);
        }
      } else {
        setError(response.data.message || "Erreur lors du chargement");
      }
    } catch (error: any) {
      console.error("Erreur chargement événements:", error);
      if (error.response?.status === 403) {
        setError("Session expirée. Veuillez vous reconnecter.");
      } else {
        setError("Erreur de connexion au serveur");
      }
    }
  }, [eventType, fetchTables]);

  const fetchAnniversaires = useCallback(async () => {
    try {
      const response = await api.get<AnniversairesResponse>("/protocole/anniversaires");

      if (response.data.success) {
        setAnniversaires(response.data.anniversaires);
        if (response.data.anniversaires.length === 1 && eventType === "anniversaires") {
          const firstId = response.data.anniversaires[0]._id;
          setSelectedId(firstId);
          fetchTables(firstId);
        }
      } else {
        setError(response.data.message || "Erreur lors du chargement");
      }
    } catch (error: any) {
      console.error("Erreur chargement anniversaires:", error);
      if (error.response?.status === 403) {
        setError("Session expirée. Veuillez vous reconnecter.");
      } else {
        setError("Erreur de connexion au serveur");
      }
    }
  }, [eventType, fetchTables]);

  const handleInstall = useCallback(async (inviteId: string) => {
    try {
      let url = "";
      if (eventType === "mariages") {
        url = `/protocole/install/${inviteId}`;
      } else if (eventType === "evenements") {
        url = `/protocole/evenements/confirm/${inviteId}`;
      } else {
        url = `/protocole/anniversaires/confirm/${inviteId}`;
      }
      
      const response = await api.put<InstallResponse>(url);

      if (response.data.success) {
        if (selectedId) {
          fetchTables(selectedId);
        }
        
        setNotification({
          message: `${response.data.invite.nom} a été confirmé`,
          invite: {
            nom: response.data.invite.nom,
            table: '',
            heure: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
          }
        });
        
        setTimeout(() => setNotification(null), 3000);
      } else {
        alert(response.data.message || "Erreur lors de la confirmation");
      }
    } catch (error) {
      console.error("Erreur confirmation:", error);
      alert("Erreur de connexion au serveur");
    }
  }, [eventType, selectedId, fetchTables]);

  const loadData = useCallback(() => {
    setLoading(true);
    if (eventType === "mariages") {
      fetchMariages();
    } else if (eventType === "evenements") {
      fetchEvenements();
    } else {
      fetchAnniversaires();
    }
    setLoading(false);
  }, [eventType, fetchMariages, fetchEvenements, fetchAnniversaires]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectEvent = (id: string) => {
    setSelectedId(id);
    fetchTables(id);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const toggleTable = (tableName: string) => {
    setExpandedTable(expandedTable === tableName ? null : tableName);
  };

  const getTotalArrives = () => {
    return tables.reduce((sum, table) => sum + table.arrives.length, 0);
  };

  const getTotalAttendus = () => {
    return tables.reduce((sum, table) => sum + table.attendus.length, 0);
  };

  const getSelectedList = () => {
    if (eventType === "mariages") return mariages;
    if (eventType === "evenements") return evenements;
    return anniversaires;
  };

  const getItemLabel = (item: any) => {
    if (eventType === "mariages") {
      return `${item.bride} & ${item.groom} - ${formatDate(item.date)}`;
    }
    if (eventType === "evenements") {
      const typeLabels: Record<string, string> = {
        bapteme: "Baptême",
        fete: "Fête",
        conference: "Conférence",
        autre: "Événement"
      };
      return `${typeLabels[item.type] || item.type} - ${formatDate(item.dateISO)}`;
    }
    return `${item.personne} (${item.age} ans) - ${formatDate(item.dateISO)}`;
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={() => navigate("/")} className="btn-login">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" fill="currentColor"/>
          </svg>
          Retour à la connexion
        </button>
      </div>
    );
  }

  const selectedList = getSelectedList();

  return (
    <div className="protocole-container">
      {notification && (
        <div className="notification-popup">
          <div className="notification-content">
            <span className="notification-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
              </svg>
            </span>
            <div className="notification-text">
              <strong>{notification.message}</strong>
              <small>Table {notification.invite.table} - {notification.invite.heure}</small>
            </div>
            <button onClick={() => setNotification(null)} className="notification-close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="header">
        <div className="header-top">
          <h1>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
            </svg>
            Protocole
          </h1>
          <button onClick={handleLogout} className="logout-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor"/>
            </svg>
            Déconnexion
          </button>
        </div>
        <p className="subtitle">Suivi des arrivées en temps réel</p>
      </div>

      <div className="event-tabs">
        <button 
          className={`tab-btn ${eventType === "mariages" ? "active" : ""}`}
          onClick={() => setEventType("mariages")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 9V7c0-1.1-.9-2-2-2h-4c0-1.66-1.34-3-3-3S8 3.34 8 5H4c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h4c0 1.66 1.34 3 3 3s3-1.34 3-3h4c1.1 0 2-.9 2-2z" fill="currentColor"/>
          </svg>
          Mariages
        </button>
        <button 
          className={`tab-btn ${eventType === "evenements" ? "active" : ""}`}
          onClick={() => setEventType("evenements")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
          </svg>
          Événements
        </button>
        <button 
          className={`tab-btn ${eventType === "anniversaires" ? "active" : ""}`}
          onClick={() => setEventType("anniversaires")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 6c1.11 0 2-.9 2-2 0-.38-.1-.73-.29-1.03L12 0l-1.71 2.97c-.19.3-.29.65-.29 1.03 0 1.1.9 2 2 2zm4.6 4.47l-1.07-1.86L13 11.6V17h2v-4.4l1.6-2.13zM17 22v-2h-4v-4h-2v4H7v2h10zM8 12.4L5.6 9.6 4 11.6V17h2v-4.4l2-2.2z" fill="currentColor"/>
          </svg>
          Anniversaires
        </button>
      </div>

      {selectedList.length > 1 && (
        <div className="event-selector">
          <label htmlFor="event-select">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
            </svg>
            Choisir un {eventType === "mariages" ? "mariage" : eventType === "evenements" ? "événement" : "anniversaire"} :
          </label>
          <select
            id="event-select"
            value={selectedId || ""}
            onChange={(e) => handleSelectEvent(e.target.value)}
            className="event-select"
          >
            <option value="" disabled>Sélectionnez</option>
            {selectedList.map((item: any) => (
              <option key={item._id} value={item._id}>
                {getItemLabel(item)}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedId && (
        <>
          <div className="stats-globales">
            <div className="stat-global">
              <span className="stat-label">Tables</span>
              <span className="stat-value">{tables.length}</span>
            </div>
            <div className="stat-global">
              <span className="stat-label">Arrivés</span>
              <span className="stat-value">{getTotalArrives()}</span>
            </div>
            <div className="stat-global">
              <span className="stat-label">Attendus</span>
              <span className="stat-value">{getTotalAttendus()}</span>
            </div>
          </div>

          {loadingTables ? (
            <div className="loading-tables">Chargement des tables...</div>
          ) : tables.length === 0 ? (
            <p className="empty-message">Aucune table assignée</p>
          ) : (
            <div className="tables-list">
              {tables.map((table) => (
                <div key={table.name} className="table-card">
                  <div 
                    className="table-header"
                    onClick={() => toggleTable(table.name)}
                  >
                    <div className="table-title">
                      <h3>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                        </svg>
                        Table {table.name}
                      </h3>
                      <span className="table-count">
                        {table.arrives.length}/{table.total}
                      </span>
                    </div>
                    <div className="table-progress">
                      <div 
                        className="progress-bar"
                        style={{ 
                          width: `${(table.arrives.length / table.total) * 100}%` 
                        }}
                      ></div>
                    </div>
                    <button className="toggle-btn">
                      {expandedTable === table.name ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" fill="currentColor"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="currentColor"/>
                        </svg>
                      )}
                    </button>
                  </div>

                  {expandedTable === table.name && (
                    <div className="table-details">
                      <div className="arrives-section">
                        <h4>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                          </svg>
                          Confirmés ({table.arrives.length})
                        </h4>
                        {table.arrives.length === 0 ? (
                          <p className="empty-list">Aucun confirmé</p>
                        ) : (
                          <ul className="arrives-list">
                            {table.arrives.map((invite: InviteArrive) => (
                              <li key={invite._id} className="arrive-item">
                                <div className="arrive-info">
                                  <span className="arrive-nom">{invite.nom}</span>
                                  <span className="arrive-heure">{invite.heure}</span>
                                </div>
                                {invite.statut !== 'installed' && (
                                  <button
                                    onClick={() => handleInstall(invite._id)}
                                    className="install-btn"
                                    title="Marquer comme installé"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                                    </svg>
                                    Installer
                                  </button>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="attendus-section">
                        <h4>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
                          </svg>
                          Attendus ({table.attendus.length})
                        </h4>
                        {table.attendus.length === 0 ? (
                          <p className="empty-list">Tous les invités sont arrivés !</p>
                        ) : (
                          <ul className="attendus-list">
                            {table.attendus.map((invite: InviteAttendu) => (
                              <li key={invite._id} className="attendu-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
                                </svg>
                                {invite.nom}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProtocoleDashboard;